<template>
  <section class="mb-[2.8571428571rem]">
    <div class="mb-6">
      <Heading :level="5" color="text-ash-200">
        {{ title }}
      </Heading>
    </div>
    <hr class="border-gray-300 my-6" />

    <fieldset :aria-label="title">
      <legend class="sr-only">{{ title }}</legend>
      <ul class="space-y-[0.75rem]">
        <li
          class="mb-[0.2142857rem] checkbox-component"
          v-for="(option, i) in displayedOptions"
          :key="option.value"
        >
          <label
            class="flex items-center gap-[1rem] cursor-pointer"
            :class="{
              'opacity-50 cursor-not-allowed': option.count === 0
            }"
            :aria-disabled="option.count === 0 ? 'true' : 'false'"
            :title="option.count === 0 ? 'Unavailable for current filters' : ''"
          >
            <input
              type="checkbox"
              class="accent-ash-500 w-[1rem] h-[1rem] border-red-500"
              :value="option.value"
              :checked="modelValue.includes(option.value)"
              :disabled="option.count === 0"
              @change="handleChange(option.value, option.count)"
              @keydown.enter.prevent="handleChange(option.value, option.count)"
              :id="`checkbox_${index}_${i}`"
            />
            <span class="text-[1rem] text-ash-100">
              {{ option.value }}
            </span>
          </label>
        </li>

        <li v-if="hasToggle" class="see-more">
          <span
            id="see-more-options"
            title="Toggle options"
            role="button"
            tabindex="0"
            class="flex items-center gap-2 ml-[0.2142857143rem] text-ash-100 text-[1rem] hover:text-gold-100 hover:underline cursor-pointer"
            :aria-expanded="showAll"
            :aria-label="`${showAll ? 'See less' : 'See more'} ${title} options`"
            @click.stop="toggleShowAll"
            @keydown.enter.stop="toggleShowAll"
            @keydown.space.stop.prevent="toggleShowAll"
          >
            <Icon
              :icon="showAll ? 'fa-minus' : 'fa-plus'"
              size="sm"
              aria-hidden="true"
            />
            {{ showAll ? 'See Less' : 'See More' }}
          </span>
        </li>
      </ul>
    </fieldset>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import Heading from '../../atom/heading.vue'
import type { FacetsValue } from '~~/shared/types/common'
import Icon from "../../atom/icon.vue"

const props = defineProps<{
  title: string
  options: FacetsValue[]
  modelValue: string[]
  visibleCount?: number
  index: number
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const showAll = ref(false)

const displayedOptions = computed(() => {
  if (!props.visibleCount || showAll.value) {
    return props.options
  }
  return props.options.slice(0, props.visibleCount)
})

const hasToggle = computed(() => {
  return !!props.visibleCount && props.options.length > (props.visibleCount ?? 0)
})

const toggleShowAll = () => {
  showAll.value = !showAll.value
  nextTick(() => {
    if (showAll.value && props.visibleCount) {
      const newId = `checkbox_${props.index}_${props.visibleCount}`
      const firstNew = document.getElementById(newId) as HTMLInputElement | null
      if (firstNew && !firstNew.disabled) { 
         setTimeout(() => firstNew.focus(), 0) 
      }
    }
  })
}

const handleChange = (value: string, count?: number) => {
  if (count === 0) return

  const next = [...props.modelValue]
  const idx = next.indexOf(value)
  if (idx > -1) {
    next.splice(idx, 1)
  } else {
    next.push(value)
  }
  emit('update:modelValue', next)
}
</script>
